import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {Wrapper, TheHead, Content, ArrowDown, ArrowUp} from './Filter.style';
import {FILTERS, FILTERS_LIMIT, FILTERS_NAME} from '../../constants/constants';
import FilterItem from './FilterItem';
import {getCategoriesSize} from '../../helpers/data.helper';

export default class Filter extends PureComponent {
  static propTypes = {
    filter: PropTypes.object.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    isCategorySearch: PropTypes.bool.isRequired,
    isRoot: PropTypes.bool.isRequired,
    openStatus: PropTypes.object,
    toggleStatus: PropTypes.func,
    filterCounts: PropTypes.array,
  };

  constructor(props) {
    super(props);
    const {filter} = props;
    const isCategoryList = filter.name === FILTERS_NAME.categoryList;
    let totalCategoriesSize = 0;
    if (isCategoryList) {
      totalCategoriesSize = getCategoriesSize(filter.counts);
    }
    this.state = {
      onlyTopLevel: totalCategoriesSize > FILTERS_LIMIT,
      cachedCounts: null,
      prevSearchQuery: undefined,
      prevNonCategoryFilterKey: undefined,
      pendingSelectedId: null,
    };
  }

  // Reset the category cache synchronously whenever the search query (q) or any
  // non-category filter (brand/origin/sizeOrCut/productFeatures) changes, so the
  // updated (filtered) categories are used instead of a stale cached set.
  static getDerivedStateFromProps(props, state) {
    if (
      props.filter.name === FILTERS_NAME.categoryList &&
      (props.searchQuery !== state.prevSearchQuery ||
        props.nonCategoryFilterKey !== state.prevNonCategoryFilterKey)
    ) {
      return {
        prevSearchQuery: props.searchQuery,
        prevNonCategoryFilterKey: props.nonCategoryFilterKey,
        cachedCounts: null,
      };
    }
    return null;
  }

  componentDidUpdate(prevProps) {
    const {filter, filterCounts} = this.props;
    const isCategoryList = filter.name === FILTERS_NAME.categoryList;
    if (filter !== prevProps.filter) {
      this.setState({pendingSelectedId: null});
    }
    if (filter !== prevProps.filter && isCategoryList) {
      const totalCategoriesSize = getCategoriesSize(filter.counts);
      this.setState({onlyTopLevel: totalCategoriesSize > FILTERS_LIMIT});
    }
    if (
      isCategoryList &&
      filterCounts &&
      filterCounts.length > 0 &&
      filterCounts !== prevProps.filterCounts
    ) {
      // Only replace the cache when the incoming data is at the root level —
      // a drilled-down response has fewer items, so we keep the existing cache.
      const cachedLen = this.state.cachedCounts
        ? this.state.cachedCounts.length
        : 0;
      if (filterCounts.length >= cachedLen) {
        this.setState({cachedCounts: filterCounts});
      }
    }
  }

  // Build a flat id→selected map from the current API response, then apply it
  // onto the cached root-level structure so we always show all root categories
  // but reflect the latest selection state.
  mergeSelectionState = (cachedCounts, currentCounts) => {
    if (!currentCounts || currentCounts.length === 0) return cachedCounts;

    const selectedMap = {};
    const buildMap = items => {
      items.forEach(item => {
        selectedMap[item.id] = !!item.selected;
        if (item.counts && item.counts.length > 0) buildMap(item.counts);
      });
    };
    buildMap(currentCounts);

    if (Object.keys(selectedMap).length === 0) return cachedCounts;

    const applySelection = items =>
      items.map(item => ({
        ...item,
        selected: item.id in selectedMap ? selectedMap[item.id] : item.selected,
        counts:
          item.counts && item.counts.length > 0
            ? applySelection(item.counts)
            : item.counts,
      }));

    return applySelection(cachedCounts);
  };

  toggle = () => this.props.toggleStatus(this.props.filter.name);

  handleFilterChange = (item, updatedParams) => {
    const isSelecting = !item.selected;
    this.setState({pendingSelectedId: isSelecting ? item.id : null});
    this.props.onFilterChange(updatedParams);
  };

  render() {
    const {
      filter,
      filterCounts,
      isRoot,
      isApplied,
      index,
      isCategorySearch,
      openStatus,
    } = this.props;
    const {pendingSelectedId} = this.state;
    const isCategoryList = filter.name === FILTERS_NAME.categoryList;
    const effectiveCounts =
      isCategoryList && this.state.cachedCounts
        ? this.mergeSelectionState(this.state.cachedCounts, filterCounts)
        : filterCounts;
    const renderItems =
      effectiveCounts &&
      effectiveCounts.map(item => {
        const isChecked =
          pendingSelectedId !== null
            ? item.id === pendingSelectedId
            : item.selected;
        return (
          <FilterItem
            key={item.id + item.query + (this.props.searchQuery || '')}
            item={item}
            onCheckboxChange={updatedParams =>
              this.handleFilterChange(item, updatedParams)
            }
            isRoot={isRoot}
            isChecked={isChecked}
            parentIndex={item.id + index}
            parentQuery={item.query}
            onlyTopLevel={this.state.onlyTopLevel}
            isCategorySearch={isCategorySearch}
          />
        );
      });
    const renderArrow =
      filter &&
      filter.counts &&
      filter.counts.length > 0 &&
      !!openStatus[filter.name] ? (
        <ArrowDown />
      ) : (
        <ArrowUp />
      );
    return (
      <Wrapper>
        <TheHead onClick={this.toggle} $isApplied={isApplied}>
          {FILTERS[filter.name]}
          {renderArrow}
        </TheHead>
        <Content $isShown={openStatus[filter.name]}>{renderItems}</Content>
      </Wrapper>
    );
  }
}
